import { supabase } from '../lib/supabaseClient';
import type { NewsPost, BlogComment, CreateNewsPostInput, UpdateNewsPostInput } from '../types/news';

const NEWS_BUCKET = 'blog-images';

const DEFAULT_PAGE_SIZE = 20;

/**
 * Fetch a map of user_id -> username for a set of author IDs.
 */
async function fetchAuthorUsernames(authorIds: string[]): Promise<Map<string, string>> {
  if (authorIds.length === 0) return new Map();

  const { data } = await supabase
    .from('profiles')
    .select('user_id, username')
    .in('user_id', authorIds);

  const map = new Map<string, string>();
  for (const row of data ?? []) {
    map.set(row.user_id as string, row.username as string);
  }
  return map;
}

/**
 * Fetch news posts with pagination. Ordered by newest first.
 */
export async function fetchNewsPosts(
  offset = 0,
  limit = DEFAULT_PAGE_SIZE
): Promise<{ data: NewsPost[]; error: Error | null; hasMore: boolean }> {
  const { data, error } = await supabase
    .from('blog')
    .select('id, title, content, image_urls, author_id, created_at, updated_at')
    .order('created_at', { ascending: false })
    .range(offset, offset + limit - 1);

  if (error) {
    return { data: [], error, hasMore: false };
  }

  const rows = data || [];
  const hasMore = rows.length === limit;

  const authorIds = Array.from(new Set(rows.map((r: Record<string, unknown>) => r.author_id as string)));
  const usernameMap = await fetchAuthorUsernames(authorIds);

  const posts: NewsPost[] = rows.map((row: Record<string, unknown>) => {
    const urls = row.image_urls as string[] | null | undefined;
    const image_urls = Array.isArray(urls) ? urls : [];
    return {
      id: row.id as string,
      title: row.title as string,
      content: row.content as string,
      image_urls,
      author_id: row.author_id as string,
      author_display_name: usernameMap.get(row.author_id as string),
      created_at: row.created_at as string,
      updated_at: row.updated_at as string | undefined,
    };
  });

  return { data: posts, error: null, hasMore };
}

/**
 * Fetch a single blog post by ID.
 */
export async function fetchBlogPostById(
  id: string
): Promise<{ data: NewsPost | null; error: Error | null }> {
  const { data, error } = await supabase
    .from('blog')
    .select('id, title, content, image_urls, author_id, created_at, updated_at')
    .eq('id', id)
    .single();

  if (error) {
    return { data: null, error };
  }

  const row = data as Record<string, unknown>;
  const urls = row.image_urls as string[] | null | undefined;

  const usernameMap = await fetchAuthorUsernames([row.author_id as string]);

  return {
    data: {
      id: row.id as string,
      title: row.title as string,
      content: row.content as string,
      image_urls: Array.isArray(urls) ? urls : [],
      author_id: row.author_id as string,
      author_display_name: usernameMap.get(row.author_id as string),
      created_at: row.created_at as string,
      updated_at: row.updated_at as string | undefined,
    },
    error: null,
  };
}

/**
 * Create a new news post. Requires authenticated user.
 */
export async function createNewsPost(
  input: CreateNewsPostInput,
  authorId: string
): Promise<{ data: NewsPost | null; error: Error | null }> {
  const { data, error } = await supabase
    .from('blog')
    .insert({
      title: input.title,
      content: input.content,
      image_urls: input.image_urls ?? [],
      author_id: authorId,
    })
    .select()
    .single();

  if (error) {
    return { data: null, error };
  }

  return {
    data: data as NewsPost,
    error: null,
  };
}

/**
 * Update a news post. Only the author can update.
 */
export async function updateNewsPost(
  postId: string,
  input: UpdateNewsPostInput
): Promise<{ data: NewsPost | null; error: Error | null }> {
  const { data, error } = await supabase
    .from('blog')
    .update({
      title: input.title,
      content: input.content,
      image_urls: input.image_urls,
    })
    .eq('id', postId)
    .select()
    .single();

  if (error) {
    return { data: null, error };
  }

  return {
    data: data as NewsPost,
    error: null,
  };
}

/**
 * Delete a news post. Only the author can delete.
 */
export async function deleteNewsPost(postId: string): Promise<{ error: Error | null }> {
  const { error } = await supabase
    .from('blog')
    .delete()
    .eq('id', postId);

  return { error };
}

/**
 * Upload an image for a news post. Returns the public URL.
 * Uses userId in path so RLS can enforce user-only delete.
 */
export async function uploadNewsImage(
  file: File,
  userId: string
): Promise<{ url: string | null; error: string | null }> {
  const fileExt = file.name.split('.').pop()?.toLowerCase() || 'png';
  const fileName = `${Date.now()}-${Math.random().toString(36).slice(2)}.${fileExt}`;
  const filePath = `${userId}/${fileName}`;

  const { error: uploadError } = await supabase.storage
    .from(NEWS_BUCKET)
    .upload(filePath, file, {
      cacheControl: '3600',
      upsert: false,
    });

  if (uploadError) {
    return { url: null, error: uploadError.message };
  }

  const { data: urlData } = supabase.storage
    .from(NEWS_BUCKET)
    .getPublicUrl(filePath);

  return { url: urlData.publicUrl, error: null };
}

// ---------------------------------------------------------------------------
// Comments
// ---------------------------------------------------------------------------

/**
 * Fetch comments for a post. Returns oldest-first.
 * Pass `limit` to fetch only the first N (for preview mode).
 */
export async function fetchComments(
  postId: string,
  limit?: number
): Promise<{ data: BlogComment[]; error: Error | null; total: number }> {
  // First get total count
  const { count } = await supabase
    .from('blog_comments')
    .select('id', { count: 'exact', head: true })
    .eq('post_id', postId);

  let query = supabase
    .from('blog_comments')
    .select('id, post_id, author_id, content, created_at')
    .eq('post_id', postId)
    .order('created_at', { ascending: true });

  if (limit !== undefined) {
    query = query.limit(limit);
  }

  const { data, error } = await query;

  if (error) {
    return { data: [], error, total: 0 };
  }

  const rows = (data ?? []) as Record<string, unknown>[];
  const authorIds = Array.from(new Set(rows.map((r) => r.author_id as string)));
  const usernameMap = await fetchAuthorUsernames(authorIds);

  const comments: BlogComment[] = rows.map((row) => ({
    id: row.id as string,
    post_id: row.post_id as string,
    author_id: row.author_id as string,
    author_display_name: usernameMap.get(row.author_id as string),
    content: row.content as string,
    created_at: row.created_at as string,
  }));

  return { data: comments, error: null, total: count ?? 0 };
}

/**
 * Add a comment to a post. Requires authenticated user.
 */
export async function addComment(
  postId: string,
  authorId: string,
  content: string
): Promise<{ data: BlogComment | null; error: Error | null }> {
  const { data, error } = await supabase
    .from('blog_comments')
    .insert({ post_id: postId, author_id: authorId, content })
    .select('id, post_id, author_id, content, created_at')
    .single();

  if (error) {
    return { data: null, error };
  }

  const row = data as Record<string, unknown>;
  const usernameMap = await fetchAuthorUsernames([row.author_id as string]);

  return {
    data: {
      id: row.id as string,
      post_id: row.post_id as string,
      author_id: row.author_id as string,
      author_display_name: usernameMap.get(row.author_id as string),
      content: row.content as string,
      created_at: row.created_at as string,
    },
    error: null,
  };
}

/**
 * Delete a comment. Supabase RLS ensures only the author can delete their own.
 */
export async function deleteComment(commentId: string): Promise<{ error: Error | null }> {
  const { error } = await supabase
    .from('blog_comments')
    .delete()
    .eq('id', commentId);

  return { error };
}

// ---------------------------------------------------------------------------
// Likes
// ---------------------------------------------------------------------------

/**
 * Fetch like count for a post and whether the current user has liked it.
 */
export async function fetchLikes(
  postId: string,
  userId?: string
): Promise<{ count: number; liked: boolean }> {
  const { count } = await supabase
    .from('blog_likes')
    .select('id', { count: 'exact', head: true })
    .eq('post_id', postId);

  let liked = false;
  if (userId) {
    const { data } = await supabase
      .from('blog_likes')
      .select('id')
      .eq('post_id', postId)
      .eq('user_id', userId)
      .maybeSingle();
    liked = !!data;
  }

  return { count: count ?? 0, liked };
}

/**
 * Toggle like for the current user. Returns new state.
 */
export async function toggleLike(
  postId: string,
  userId: string,
  currentlyLiked: boolean
): Promise<{ count: number; liked: boolean; error: Error | null }> {
  if (currentlyLiked) {
    const { error } = await supabase
      .from('blog_likes')
      .delete()
      .eq('post_id', postId)
      .eq('user_id', userId);

    if (error) {
      const { count } = await supabase
        .from('blog_likes')
        .select('id', { count: 'exact', head: true })
        .eq('post_id', postId);
      return { count: count ?? 0, liked: true, error };
    }
  } else {
    const { error } = await supabase
      .from('blog_likes')
      .insert({ post_id: postId, user_id: userId });

    if (error) {
      const { count } = await supabase
        .from('blog_likes')
        .select('id', { count: 'exact', head: true })
        .eq('post_id', postId);
      return { count: count ?? 0, liked: false, error };
    }
  }

  const { count } = await supabase
    .from('blog_likes')
    .select('id', { count: 'exact', head: true })
    .eq('post_id', postId);

  return { count: count ?? 0, liked: !currentlyLiked, error: null };
}
